import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Share } from 'lucide-react';

import { formatAmountWithCurrency } from '../../../helpers/commonHelper';
import { StatsCard } from '../StatsCard/StatsCard';

import { ShareModal } from './ShareModal/ShareModal';

export const StatsOverview = ({ currentOrganization, currentUser, stats }) => {
  const { t } = useTranslation('dashboard');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const [referralUrl, setReferralUrl] = useState('loading...');

  useEffect(() => {
    if (currentOrganization) {
      setReferralUrl(`${currentOrganization?.employer_job_link}?rp=${currentUser?.slug}`);
    }
  }, [currentOrganization, currentUser?.slug]);

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    } catch (error) {
      // Silently fail if clipboard access is denied
    }
  };

  return (
    <div className="space-y-4">
      {currentOrganization?.name !== 'Boon' && (
        <div className="p-3 bg-white rounded-lg sm:p-4 shadow-card">
          <p className="text-sm font-medium text-gray-700 mb-1.5">{t('stats.myReferralLink')}</p>
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-[14px] py-2 hover:cursor-pointer">
            <div className="flex items-center w-full min-w-0">
              <button
                type="button"
                className="w-full min-w-0 p-0 overflow-hidden text-sm text-left text-gray-500 transition-colors bg-transparent border-none hover:text-gray-700 hover:cursor-pointer text-ellipsis whitespace-nowrap"
                onClick={handleCopyClick}
              >
                {isCopied && <Check className="w-4 h-4" />}
                {!isCopied ? referralUrl : ` ${t('stats.copied')}`}
              </button>
              <button
                type="button"
                onClick={() => setIsShareModalOpen(true)}
                className="p-0 ml-2 text-gray-500 transition-colors bg-transparent border-none hover:text-gray-700"
              >
                <Share className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-3 space-y-3 bg-white rounded-xl shadow-card">
        <div className="w-full text-lg font-semibold text-gray-900">{t('stats.overview')}</div>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 ">
          <StatsCard
            title={t('stats.totalEarned')}
            amount={`${formatAmountWithCurrency(stats?.earnings.earned.total, stats?.earnings.earned.currency)}`}
            type="earned"
            breakdown={stats?.earnings.earned.breakdown}
            prefferedCurrency={stats?.earnings.earned.currency}
          />
          <StatsCard
            title={t('stats.pendingPayments')}
            amount={`${formatAmountWithCurrency(stats?.earnings.pending.total, stats?.earnings.pending.currency)}`}
            type="pending"
            breakdown={stats?.earnings.pending.breakdown}
            prefferedCurrency={stats?.earnings.pending.currency}
          />
          <StatsCard
            title={t('stats.paid')}
            amount={`${formatAmountWithCurrency(stats?.earnings.paid.total, stats?.earnings.paid.currency)}`}
            type="paid"
            breakdown={stats?.earnings.paid.breakdown}
            prefferedCurrency={stats?.earnings.paid.currency}
          />
          <StatsCard
            title={t('stats.potential')}
            amount={`${formatAmountWithCurrency(stats?.earnings.potential.total, stats?.earnings.potential.currency)}`}
            type="potential"
            breakdown={stats?.earnings.potential.breakdown}
            prefferedCurrency={stats?.earnings.potential.currency}
          />
          <StatsCard title={t('stats.referrals')} amount={stats?.referrals.total} type="referrals" />
          <StatsCard title={t('stats.hires')} amount={stats?.referrals.hired} type="hires" />
        </div>
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={t('referralLink.myReferralLink')}
        description={t('referralLink.shareDescription')}
        url={referralUrl}
        showModal={isShareModalOpen}
        currentOrganization={currentOrganization}
      />
    </div>
  );
};
